// Converte um report do router em objeto de ordem.
// Extraído de orderStore p/ testabilidade isolada (#138) — função pura, sem o
// grafo/ciclo de orderStore↔ordersRouter.

// #441 — pernas GANHO/PERDA a partir dos gatilhos da ATM que o router passou a expor
// no rpt da ENTRADA (OrdersRouter dfc3e0d: Report.gainTrigger/lossTrigger).
//
// Por que sintetizar em vez de receber um array pronto: numa OTO as pernas NÃO são
// ordens no broker até a entrada executar (a proteção só é enviada no fill), então não
// existe report delas para o servidor mandar — os preços vivem só na ATMStrategy. É a
// mesma síntese que ADOpenOrder.GetFromOTO faz do lado do snapshot; espelhamos o
// formato dele (ids -L/-S, `oco` cruzado, type profit/stop) para a UI não distinguir
// a origem: quem desenha e agrupa GANHO/PERDA continua vendo a mesma forma.
//
// Só entra quando o report NÃO traz `oto` próprio — accountData e pendingOTO local
// continuam tendo precedência.
const buildOtoFromAtmTriggers = (report, { orderSymbol, side, quantity }) => {
  const gain = Number(report.gainTrigger) || 0;
  const loss = Number(report.lossTrigger) || 0;
  if (gain <= 0 && loss <= 0) return undefined;

  // Pernas fecham a posição → lado oposto ao da entrada (igual GetFromOTO).
  const legSide = side === "buy" ? "sell" : "buy";
  const gainId = `${report.id}-L`;
  const lossId = `${report.id}-S`;
  const legs = [];

  if (gain > 0) {
    legs.push({
      id: gainId,
      symbol: orderSymbol,
      side: legSide,
      quantity,
      price: gain,
      limit: gain,
      stop: null,
      orderType: "LIMIT",
      type: "profit",
      state: "open",
      isRouterOrder: true,
      oco: loss > 0 ? lossId : null,
    });
  }
  if (loss > 0) {
    legs.push({
      id: lossId,
      symbol: orderSymbol,
      side: legSide,
      quantity,
      price: loss,
      limit: null,
      stop: loss,
      orderType: "STOPLIMIT",
      type: "stop",
      state: "open",
      isRouterOrder: true,
      oco: gain > 0 ? gainId : null,
    });
  }
  return legs;
};

// Convert router report to order format for immediate display
export const convertReportToOrder = (report, symbol) => {
  if (!report?.id) return null;

  // Use provided symbol or fall back to report.symbol
  const orderSymbol = symbol || report.symbol;
  if (!orderSymbol) return null;

  const side = (
    report.side ||
    report.action ||
    report.type ||
    "buy"
  ).toLowerCase();
  const limitPrice = report.limit || 0;
  const stopPrice = report.stop || 0;
  const price = limitPrice || stopPrice || report.price || 0;

  // FIX #138: Reject orders with invalid/zero price to prevent ghost orders during OCO drag
  if (!price || price <= 0 || !isFinite(price)) {
    console.warn(
      "⚠️ convertReportToOrder: Rejecting order with invalid price:",
      {
        orderId: report.id,
        symbol: orderSymbol,
        limit: report.limit,
        stop: report.stop,
        price: report.price,
      },
    );
    return null;
  }

  const quantity = report.quantity || report.qty || report.qtt || 1;

  // Determine order type
  let orderType = "MARKET";
  if (stopPrice > 0) {
    orderType = "STOPLIMIT";
  } else if (limitPrice > 0) {
    orderType = "LIMIT";
  }

  // Map state
  const status = report.state || report.OrdStatus || "";
  let state = "open";
  switch (status.toLowerCase?.() || status) {
    case "0": // New
    case "1": // Partially filled
    case "5": // Pending
    case "new":
    case "pending":
    case "open":
    case "active":
    case "replaced":
      state = "open";
      break;
    case "partial":
      state = "partial";
      break;
    default:
      state = "open";
  }

  // Handle OTO legs if present in report
  let oto;
  if (!Array.isArray(report.oto) || report.oto.length === 0) {
    // #441: sem `oto` no report — tenta os gatilhos da ATM (router >= 2.0.1).
    // Router antigo não manda os campos → undefined, comportamento idêntico ao de antes.
    oto = buildOtoFromAtmTriggers(report, { orderSymbol, side, quantity });
  }
  if (Array.isArray(report.oto) && report.oto.length > 0) {
    oto = report.oto.map((leg) => {
      const legSide = (
        leg.side ||
        leg.action ||
        (side === "buy" ? "sell" : "buy")
      ).toLowerCase();
      const legOrderType = leg.stop && leg.stop > 0 ? "STOPLIMIT" : "LIMIT";
      const otoType = legOrderType === "STOPLIMIT" ? "stop" : "profit";

      return {
        id: leg.id || `${report.id}_OTO_${Date.now()}`,
        symbol: leg.symbol || orderSymbol,
        side: legSide,
        quantity: leg.quantity || leg.qty || leg.qtt || quantity,
        price: leg.limit || leg.stop || leg.price || 0,
        limit: leg.limit,
        stop: leg.stop,
        orderType: legOrderType,
        type: otoType,
        state: (leg.state || leg.status || "open").toLowerCase(),
        isRouterOrder: true,
      };
    });
  }

  // Build originalRouterOrder from report data (needed for replace operations)
  const originalRouterOrder = {
    id: report.id,
    symbol: orderSymbol,
    action: side,
    side: side,
    quantity: quantity,
    limit: limitPrice || null,
    stop: stopPrice || null,
    price: price,
    tif: report.tif || "DAY",
    // #441: espelha o que o accountData já entrega aqui (o payload de cancel/replace
    // que sai do odin leva as pernas junto) — sintetizadas quando o report não as traz.
    oto: report.oto ?? oto,
    oco: report.oco,
  };

  return {
    id: report.id,
    symbol: orderSymbol,
    side: side,
    price: price,
    limit: limitPrice || null,
    stop: stopPrice || null,
    quantity: quantity,
    orderType: orderType,
    state: state,
    isRouterOrder: true,
    fromReport: true,
    oto: oto,
    oco: report.oco,
    originalRouterOrder: originalRouterOrder, // Include for replace operations
  };
};
